import { Bedriftsdata, Produkt } from "codegen";
import { ReactNode } from "react";
import styled, { css } from "styled-components";

import { faCircleInfo } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { colors } from "@ntemarked/mittne-components";

import { InnkjoepsbestillingText } from "pages/Sikring/components/InnkjoepsbestillingText";
import { MonthlyVolumeList } from "pages/Sikring/components/MonthlyVolumeList";
import { BackInTimeWarning } from "pages/Sikring/opprett/BackInTimeWarning";
import { StepHeading, StepWrapper } from "pages/Sikring/opprett/CreateSikringPage";
import { HighVolumeWarning } from "pages/Sikring/opprett/HighVolumeWarning";
import { getInteractiveFields, interactiveFieldLabel } from "pages/Sikring/opprett/Step2Contract";
import { WizardState, getFlyt, requiresContact, skipsSigning } from "pages/Sikring/opprett/wizardTypes";
import {
	PRICE_UNIT,
	SCOPE_LABEL,
	TotalDistribution,
	VolumeAndPrice,
	firstDayOfMonth,
	fmtDate,
	formatNumber,
	fmtPrice,
	fmtPriceWithUnit,
	isBackInTime,
	isDefined,
	lastDayOfMonth,
	FLYT_INFO,
	FLYT_LABEL,
} from "pages/Sikring/sikringUtils";
import { SikringMaalepunkt, SikringOmfangType } from "pages/Sikring/types";
import { maskFodselsnummer } from "pages/Strombestilling/orders/components/PersonSearch";
import { formatAddress } from "utils/Address";

interface Step4Props {
	stepNumber: number;
	wiz: WizardState;
	followupProduct?: Produkt;
	company: Bedriftsdata;
	volumeAndPrice: VolumeAndPrice;
	customerMeteringPoints?: SikringMaalepunkt[];
	update: <K extends keyof WizardState>(field: K, value: WizardState[K]) => void;
}

export function Step4Review({ stepNumber, wiz, followupProduct, company, volumeAndPrice, customerMeteringPoints, update }: Readonly<Step4Props>) {
	const { months, volumKwh, monthlyVolumes, innpris, fortjeneste, pris } = volumeAndPrice;
	// Et produkt som fortsatt er hentet for et tidligere valg vises ikke, så navn og felter alltid hører til valgt produkt.
	const selectedFollowupProduct = followupProduct?.tariffId === wiz.followupTariffId ? followupProduct : undefined;

	const scopeText =
		wiz.scope === SikringOmfangType.Maalepunkt
			? `${SCOPE_LABEL[wiz.scope]} (${wiz.maalepunktId || "ikke valgt"})`
			: `${SCOPE_LABEL[wiz.scope]} i prisområde ${wiz.prisomraade} · ${formatNumber(customerMeteringPoints?.length)} målepunkt`;

	const rows: RowProps[] = [
		{ name: "Kunde", value: `${company.navn ?? ""} (orgnr. ${company.orgnr ?? "–"})` },
		...(requiresContact(wiz)
			? [
					{ name: "Kontaktperson", value: `${wiz.contactFirstName} ${wiz.contactLastName} · ${wiz.contactMobile} · ${wiz.contactEmail}` },
					{ name: "Fakturaadresse", value: formatAddress(wiz.invoiceAddress) || "–" },
				]
			: []),
		...(skipsSigning(wiz) ? [] : [{ name: "Fødselsnummer", value: wiz.contactFodselsnummer ? maskFodselsnummer(wiz.contactFodselsnummer) : "–" }]),
		{ name: "Flyt", value: FLYT_LABEL[getFlyt(wiz)] },
		...(wiz.skipContract
			? []
			: [
					{ name: "Påslag (eks. mva)", value: `${wiz.paaslag} ${PRICE_UNIT}` },
					{ name: "Fastbeløp (eks. mva)", value: `${wiz.fastbelop} kr` },
					{ name: "Oppfølgingsprodukt", value: selectedFollowupProduct?.navn ?? wiz.followupTariffId },
					...getInteractiveFields(selectedFollowupProduct).map((field) => ({
						name: interactiveFieldLabel(field),
						value: wiz.interactiveFieldValues[field] ? `${wiz.interactiveFieldValues[field]} kr` : "–",
					})),
				]),
		{ name: "Omfang", value: scopeText },
		{
			name: "Periode",
			value: `${fmtDate(firstDayOfMonth(wiz.startMonth))} – ${fmtDate(lastDayOfMonth(wiz.endMonth))} (${months.length} ${months.length === 1 ? "måned" : "måneder"})`,
		},
		{
			name: "Volum",
			value: `${volumKwh ? formatNumber(volumKwh) : "–"} kWh ${volumeDescription(wiz)}`,
		},
		{
			name: "Volum per måned",
			value: <MonthlyVolumeList volumes={months.map((month) => ({ maaned: month.key, volumKwh: monthlyVolumes?.[month.key] }))} />,
		},
		{ name: "Kundepris (eks. mva)", value: fmtPriceWithUnit(pris) },
		{
			name: "Kjøpspris / margin (eks. mva)",
			value: `${isDefined(innpris) ? fmtPrice(innpris) : "–"} + ${isDefined(fortjeneste) ? fmtPrice(fortjeneste) : "–"} ${PRICE_UNIT}`,
		},
		{
			name: "Innkjøpsbestilling",
			value: <InnkjoepsbestillingText skipEnergiMail={wiz.skipEnergiMail} />,
			highlight: wiz.skipEnergiMail,
		},
	];

	return (
		<StepWrapper $maxWidth="720px">
			<StepHeading>Steg {stepNumber} – Kontroller og opprett</StepHeading>
			<Summary>
				<ReviewGrid>
					{rows.map((r) => (
						<Row key={r.name} {...r} />
					))}
				</ReviewGrid>
			</Summary>
			<InfoBox>
				<InfoBoxTitle>
					<FontAwesomeIcon icon={faCircleInfo} style={{ marginRight: "8px" }} />
					Verdt å vite før du oppretter
				</InfoBoxTitle>
				<InfoList>
					<li>Sikringen opprettes som en del av en ny ordre – den er ikke en selvstendig ressurs.</li>
					<li>{FLYT_INFO[getFlyt(wiz)]}</li>
					<li>Volumet sendes til UtilityCloud som én sikring per kalendermåned ({months.length} totalt).</li>
					{wiz.scope === SikringOmfangType.Kunde && (
						<li>Kundenivå: ordren avvises hvis kunden ikke har aktive målepunkt i prisområde {wiz.prisomraade}.</li>
					)}
				</InfoList>
			</InfoBox>
			{wiz.scope === SikringOmfangType.Kunde && <HighVolumeWarning volumKwh={volumKwh} months={months} meteringPoints={customerMeteringPoints ?? []} />}
			<BackInTimeWarning startMonth={wiz.startMonth} />
			<ConfirmBox $confirmed={wiz.confirmed}>
				<input type="checkbox" checked={wiz.confirmed} onChange={(e) => update("confirmed", e.target.checked)} />
				{isBackInTime(wiz.startMonth)
					? "Jeg har kontrollert opplysningene og vil opprette ordren med sikring tilbake i tid"
					: "Jeg har kontrollert opplysningene og vil opprette ordren med sikring"}
			</ConfirmBox>
		</StepWrapper>
	);
}

function volumeDescription(wiz: WizardState): string {
	if (wiz.volumeMode === "monthly") return "(lagt inn per måned)";
	return wiz.totalDistribution === TotalDistribution.Standard ? "(fordelt etter standard fordeling)" : "(fordelt flatt per time)";
}

interface RowProps {
	name: string;
	value: ReactNode;
	highlight?: boolean;
}

function Row({ name, value, highlight = false }: Readonly<RowProps>) {
	if (highlight) {
		return (
			<HighlightedRow>
				<RowName>{name}</RowName>
				<div>{value}</div>
			</HighlightedRow>
		);
	}
	return (
		<>
			<RowName>{name}</RowName>
			<div>{value}</div>
		</>
	);
}

const Summary = styled.div`
	border-radius: 9px;
	box-shadow: inset 0 0 0 1px ${colors.grey300};
	padding: 22px 26px;
`;

const REVIEW_COLUMNS = "180px 1fr";
const REVIEW_COLUMN_GAP = "18px";

const ReviewGrid = styled.div`
	display: grid;
	grid-template-columns: ${REVIEW_COLUMNS};
	row-gap: 12px;
	column-gap: ${REVIEW_COLUMN_GAP};
	font-size: 0.875rem;
	line-height: 1.5;
`;

// Spenner over begge kolonnene med samme kolonneoppsett som ReviewGrid. Negativ marg og like stor padding
// gir bakgrunnen litt luft rundt teksten uten å flytte den ut av linje med de andre radene.
const HighlightedRow = styled.div`
	grid-column: 1 / -1;
	display: grid;
	grid-template-columns: ${REVIEW_COLUMNS};
	column-gap: ${REVIEW_COLUMN_GAP};
	margin: -6px -10px;
	padding: 6px 10px;
	border-radius: 6px;
	background: var(--varsel-lys-gul);
`;

const ConfirmBox = styled.label<{ $confirmed: boolean }>`
	display: flex;
	align-items: flex-start;
	gap: 12px;
	padding: 16px 18px;
	border-radius: 9px;
	cursor: pointer;
	font-weight: 700;
	font-size: 1rem;
	color: ${colors.grey900};

	input {
		margin: 3px 0 0;
		width: 18px;
		height: 18px;
		flex-shrink: 0;
	}

	${(props) =>
		props.$confirmed
			? css`
					box-shadow: inset 0 0 0 2px ${colors.blue};
					background: ${colors.blue100};
				`
			: css`
					box-shadow: inset 0 0 0 2px ${colors.grey500};
				`}
`;

const RowName = styled.div`
	font-weight: 700;
	font-size: 0.875rem;
	color: ${colors.grey800};
`;

const InfoBox = styled.div`
	border-radius: 9px;
	background: ${colors.blue200};
	padding: 16px 22px;
`;

const InfoBoxTitle = styled.div`
	font-weight: 700;
	font-size: 0.875rem;
	color: ${colors.blue800};
	margin-bottom: 8px;
`;

const InfoList = styled.ul`
	margin: 0;
	padding-left: 20px;
	font-size: 0.875rem;
	line-height: 1.7;
	color: ${colors.blue800};
`;
